import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result } from 'antd';

import { isSessionExpired } from '../../Network/ApiService';
import { envConf } from '../../Config/envConfig';

/** Localhost and dev01: print what broke on the error page, for QA and us. */
const showErrorDetails = envConf.envType.toLowerCase() === 'development';

/**
 * Webpack could not load a code-split chunk: the tab is still running a build
 * that has since been replaced (a deploy, or a dev-server rebuild). The chunk
 * file is gone, the SPA fallback serves index.html in its place, and the lazy
 * page throws on render. A reload picks up the new build.
 */
const isChunkLoadError = (error?: Error) =>
  !!error &&
  (error.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk \S+ failed/i.test(error.message));

const CHUNK_RELOAD_KEY = 'fp-chunk-reload-at';

/**
 * Reloads once for a stale build. If the last such reload was under a minute
 * ago the chunk is really missing, so this refuses and the error page shows
 * instead of a reload loop (as it does when storage is unavailable).
 */
const reloadForNewBuild = () => {
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - lastReload < 60000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  /** Where in the tree it broke; only shown when showErrorDetails. */
  componentStack?: string;
  /** A chunk failed again right after a reload; show the error page. */
  chunkReloadRefused?: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  // eslint-disable-next-line react/static-property-placement
  public static defaultProps = {
    children: null,
    fallback: null
  };

  // eslint-disable-next-line react/state-in-constructor
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // We could log the error to an error reporting service like Sentry here
    console.error("Uncaught error:", error, errorInfo);
    if (showErrorDetails) {
      this.setState({ componentStack: errorInfo.componentStack ?? undefined });
    }
    if (isChunkLoadError(error) && !reloadForNewBuild()) {
      this.setState({ chunkReloadRefused: true });
    }
  }

  public render() {
    const { hasError, error, componentStack, chunkReloadRefused } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // The session just expired and the browser is loading /signin; whatever
      // broke was the page being torn down, not something to report.
      if (isSessionExpired()) {
        return null;
      }

      // A stale build is reloading into the new one.
      if (isChunkLoadError(error) && !chunkReloadRefused) {
        return null;
      }

      if (fallback) {
        return fallback;
      }
      
      return (
        <Result
          status="500"
          title="Something went wrong"
          subTitle="Sorry, an unexpected error occurred."
          extra={
            <>
              <Button type="primary" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
              {showErrorDetails && error && (
                <pre className="mt-6 mx-auto max-w-[900px] max-h-[40vh] overflow-auto p-4 text-left text-xs whitespace-pre-wrap bg-fp-surface-sunken rounded-fp-md select-text">
                  {`${error.name}: ${error.message}\n${(componentStack || '')
                    .trim()
                    .split('\n')
                    .slice(0, 12)
                    .join('\n')}`}
                </pre>
              )}
            </>
          }
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
